/**
 * The rundown editor's side panel (container): the focused row's generic
 * field form, plus the typed piece form for a piece or an adlib (its types
 * the show style's, D32: which one, and the pick, are the page's), and the
 * script and notes for a part, each change committed as a patch through
 * `commit` — the view's, which asks first when the edit touches a protected
 * on-air part (`lib/editor/onAirGuard.ts`).
 */
import type { JsonValue } from '@acp/domain/primitives.js';
import type { Row } from '@acp/domain/row.js';
import type { EditorSession } from '../api/rundownEditor.js';
import { Inspector } from '../components/editor/Inspector.js';
import { PieceForm, type ShowStylePick } from '../components/editor/PieceForm.js';
import { ScriptPanel, type TextField } from '../components/editor/ScriptPanel.js';
import { rebaseEdit } from '../lib/editor/caret.js';
import type { Build } from '../lib/editor/commands.js';
import { fieldControls } from '../lib/editor/fieldForm.js';
import { paramSetFields, setPatch, textPatch } from '../lib/editor/patches.js';
import { readFields } from '@acp/domain/fields.js';
import type { EditorPieceType } from '../lib/editor/pieceTypes.js';
import type { RowPresence } from '../lib/editor/presence.js';
import { displayName, pieceView, type RowView } from '../lib/editor/viewModel.js';

/** Applies the undoable patches `build` makes (through the on-air guard, which may rebuild them at confirmation). */
export type Commit = (build: Build) => void;

export type EditorInspectorProps = {
  readonly session: EditorSession;
  readonly commit: Commit;
  /** Undo / redo from the script and notes (Ctrl+Z there), through the on-air guard. */
  readonly step: (direction: 'undo' | 'redo') => void;
  /** The focused row as the editor shows it, and its view model; null when nothing is focused. */
  readonly focused: { readonly row: Row; readonly view: RowView } | null;
  readonly presence: readonly RowPresence[];
  readonly onFocusField: (path: string | null) => void;
  /** The show style's piece types the piece form offers, which show style that is, and the page's pick. */
  readonly pieceTypes: { readonly types: readonly EditorPieceType[]; readonly showStyle: ShowStylePick; readonly onShowStyleChange: (showStyleId: string | null) => void };
};

/** A piece's `params.*` leaves, keyed by the leaf path under `params`. */
export function paramsOf(f: Readonly<Record<string, JsonValue>>): Record<string, JsonValue> {
  const out: Record<string, JsonValue> = {};
  for (const [path, value] of Object.entries(f)) if (path.startsWith('params.')) out[path.slice('params.'.length)] = value;
  return out;
}

/**
 * A piece's or an adlib's params as the engine reads them: the `params.*` leaves expanded (`params.vs.clip` is
 * `vs: { clip }`); the raw leaves when the row does not read (a newer writer's shape).
 */
export function paramsOfRow(row: Row): Record<string, JsonValue> {
  const read = readFields(row.kind, row.f);
  if (read.ok && (read.value.kind === 'piece' || read.value.kind === 'adlib')) return { ...read.value.fields.params };
  return paramsOf(row.f);
}

type SectionProps = { readonly session: EditorSession; readonly commit: Commit; readonly step: (direction: 'undo' | 'redo') => void; readonly row: Row; readonly onFocusField: (path: string | null) => void };

function PieceSection({ session, commit, row, onFocusField, pieceTypes }: SectionProps & Pick<EditorInspectorProps, 'pieceTypes'>) {
  const set = (fields: Readonly<Record<string, JsonValue>>) => commit(() => [setPatch(session.writer, row.rowId, fields)]);
  const pieceType = typeof row.f['pieceType'] === 'string' ? row.f['pieceType'] : '';
  const timing = row.kind === 'piece' ? { start: pieceView(row).start, duration: pieceView(row).duration } : null;
  return (
    <PieceForm
      types={pieceTypes.types}
      showStyle={pieceTypes.showStyle}
      onShowStyleChange={pieceTypes.onShowStyleChange}
      pieceType={pieceType}
      params={paramsOfRow(row)}
      timing={timing}
      onTypeChange={(type) => set({ pieceType: type.id, sourceLayer: type.sourceLayer })}
      onParamCommit={(key, value) => set(paramSetFields(session.store.get(row.rowId)?.f ?? row.f, key, value))}
      onTimingCommit={(next) => set({ enable: { start: next.start, duration: next.duration } })}
      onFocusField={onFocusField}
    />
  );
}

function TextSection({ session, commit, step, row, field, presence, onFocusField }: SectionProps & { readonly field: TextField; readonly presence: readonly RowPresence[] }) {
  return (
    <ScriptPanel
      field={field}
      text={typeof row.f[field] === 'string' ? row.f[field] : ''}
      editors={presence.filter((p) => p.activity === 'editing' && p.field === field)}
      onChange={(prev, next, caret) =>
        commit(() => {
          // The row as it is NOW: a remote merge may have landed since the textarea last showed it.
          const current = session.store.get(row.rowId);
          if (current === null) return [];
          const now = typeof current.f[field] === 'string' ? current.f[field] : '';
          const patch = textPatch(session.writer, current, field, rebaseEdit(prev, next, now, caret));
          return patch === null ? [] : [patch];
        })
      }
      onFocusChange={(on) => onFocusField(on ? field : null)}
      onUndo={() => step('undo')}
      onRedo={() => step('redo')}
    />
  );
}

export function EditorInspector({ session, commit, step, focused, presence, onFocusField, pieceTypes }: EditorInspectorProps) {
  if (focused === null) return <Inspector kind={null} heading="" controls={[]} presence={[]} onCommit={() => undefined} onFocusField={onFocusField} />;
  const { row, view } = focused;
  const section = { session, commit, step, row, onFocusField };
  return (
    <Inspector
      // Another row is another form: its drafts and uncontrolled text areas start over.
      key={row.rowId}
      kind={row.kind}
      heading={displayName(view)}
      controls={fieldControls(row.kind, row.f)}
      presence={presence}
      onCommit={(path, value) => commit(() => [setPatch(session.writer, row.rowId, { [path]: value })])}
      onFocusField={onFocusField}
    >
      {row.kind === 'piece' || row.kind === 'adlib' ? <PieceSection {...section} pieceTypes={pieceTypes} /> : null}
      {row.kind === 'part' ? <TextSection {...section} field="script" presence={presence} /> : null}
      {row.kind === 'part' ? <TextSection {...section} field="notes" presence={presence} /> : null}
    </Inspector>
  );
}
